import React, { useCallback, useEffect, useRef, useState } from "react";
import { showNotification } from "@mantine/notifications";
import { AiOutlineSearch } from "react-icons/ai";

import {
  ActionIcon,
  Button,
  Input,
  Pagination,
  Select,
  Skeleton,
  Text,
  CloseButton,
  TextInput,
  Anchor,
  Modal,
  LoadingOverlay,
  Progress,
} from "@mantine/core";
import { BeatLoader } from "react-spinners";
import { DatePicker } from "@mantine/dates";
import {
  Eye,
  History,
  Plus,
  Search,
  SortAscending,
  SortDescending,
} from "tabler-icons-react";
import { formatISO } from "date-fns";
import { useDebouncedValue } from "@mantine/hooks";
import {
  deleteAgreementBulk,
  getAgreementsSigningCsvDownload,
} from "../../utilities/apis/agreements";
import {
  getValueForInput,
  loadingStates,
  csvDownload,
  formatDate,
} from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import {
  getAgreementsList,
  getAgreementTypesApi,
  getAgreementsCsvDownload,
} from "../../utilities/apis/agreements";
import UserAvatarView from "../../components/UserAvatarView";
import DashboardNotifications from "../DashboardNotifications";
import { agreementStatusColors } from "../../utilities/enums";
import AgreementAnalytics from "../../components/AgreementAnalytics/AgreementAnalytics";
import colors, { themeColor } from "../../utilities/design";
import AgreementGraph from "./AgreementGraph";
import Styles from "./AgreementsListingPage.module.css";
import { INDIA_STATES_UNIONS } from "./AgreementForm2";
import HamburgerMenu from "../HamburgerMenuAgreements";
import HamburgerMenuAgreements from "../HamburgerMenuAgreements";
import AgreementAnalytics1 from "../../components/AgreementAnalytics/AgreementAnalytics1";
import { getAgreementsInSigningList } from "../../utilities/apis/agreements";
import { useSelector } from "react-redux";
import { selectUserData } from "../../redux/selectors";
import AgreementGraph2 from "./AgreementGraph2";
import RelatedAgreementModal from "./agreementRelatedModal";
import AgreementAuditLogModal from "./AgreementAuditLogModal";
import AgreementTypeFilter from "../../components/AgreementTypeFilter";
import PartyNameFilter, {
  PARTY_NAME_LIST_TYPES,
} from "../../components/PartyNameFilter";
import { getBussinessUnit } from "../../utilities/apis/serviceProvider";
import constants from "../../utilities/constants";

const statusOptions = [
  {
    label: "Pending with Company",
    value: "Pending with Company",
  },
  {
    label: "TP",
    value: "TP",
  },
];

const estampOption = [
  {
    label: "Yes",
    value: "Yes",
  },
  {
    label: "No",
    value: "No",
  },
];

const PENDING_FROM_OPTIONS = [
  { label: constants.firstPartyName, value: constants.firstPartyName },
  { label: "Second Party", value: "Second Party" },
  { label: "Both Parties", value: "Both Parties" },
];

const INITIAL_FILTERS = {
  typeOfAgreement: null,
  mapBusinessunit: null,
  pendingFrom: null,
  secondPartyName: null,
};

const AgreementsInSigning = () => {
  const currentUser = useSelector(selectUserData);
  const listAbortRef = useRef(null);
  const csvAbortRef = useRef(null);
  const [listing, setListing] = useState({
    list: null,
    agreementsCount: null,
    loaded: false,
    sortBy: "createdAt",
    sortDirection: "desc",
    searchString: "",
    statusType: "",
    estamp: null,
    agreementType: null,
    location: null,
    startDate: null,
    endDate: null,
  });
  const [agreementTypeOptions, setAgreementTypeOptions] = useState([]);
  const [uiConfigs, setUiConfigs] = useState({
    loading: loadingStates.NO_ACTIVE_REQUEST,
    page: 1,
  });
  const [debouncedSearchInput] = useDebouncedValue(listing.searchString, 500);
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [agreementId, setAgreementId] = useState(null);
  const handleSearchClick = (id) => {
    setShowModal(true);
    setAgreementId(id);
  };
  const [showModalRelated, setShowModalRelated] = useState(false);
  const [relatedAgreementId, setRelatedAgreementId] = useState(null);
  const handlePlusClick = (id) => {
    setShowModalRelated(true);
    setRelatedAgreementId(id);
  };

  const fetchAgreementsList = async ({ page }) => {
    let filterOptions = {};
    if (listing.startDate && listing.endDate) {
      filterOptions["createdAt"] = {
        gte: formatISO(listing.startDate),
        lte: formatISO(listing.endDate),
      };
    } else if (listing.startDate || listing.endDate) {
      showNotification({
        title: "Agreement notification",
        message: "please select start and end date",
        color: "red",
      });
      return;
    }

    listAbortRef.current?.abort();
    const abortController = new AbortController();
    listAbortRef.current = abortController;

    setUiConfigs({
      ...uiConfigs,
      loading: loadingStates.LOADING,
    });
    const payload = {
      page: page ? page : uiConfigs.page,
      sortByOptions: {
        [listing.sortBy]: listing.sortDirection,
      },
    };
    if (listing.agreementType && listing.statusType && listing.estamp) {
      filterOptions = {
        ...filterOptions,
        typeOfAgreement: {
          equals: listing.agreementType,
        },
        status: {
          equals: listing.statusType,
        },
        agreementType: {
          equals: listing.estamp === "Yes" ? "eStamp" : "normal",
        },
      };
    } else if (listing.agreementType || listing.statusType || listing.estamp) {
      if (listing.agreementType) {
        filterOptions["typeOfAgreement"] = {
          equals: listing.agreementType,
        };
      }
      if (listing.statusType) {
        filterOptions["status"] = {
          equals: listing.statusType,
        };
      }
      if (listing.estamp) {
        filterOptions["agreementType"] = {
          equals: listing.estamp === "Yes" ? "eStamp" : "normal",
        };
      }
    }
    if (listing.location) {
      filterOptions["location"] = listing.location;
    }

    payload["filterOptions"] = filterOptions;
    const response = await apiWrapWithErrorWithData(
      getAgreementsList({ ...payload, signal: abortController.signal }),
    );
    if (abortController.signal.aborted) {
      return;
    }
    if (response?.success && response?.agreements) {
      setListing({
        ...listing,
        list: response?.agreements,
        agreementsCount: response.agreementsCount,
        loaded: true,
      });
    }
    setUiConfigs({
      ...uiConfigs,
      loading: loadingStates.NO_ACTIVE_REQUEST,
    });
  };
  const data = listing?.list;

  useEffect(() => {
    const abortController = new AbortController();

    const getAgreementTypes = async () => {
      const resp = await apiWrapWithErrorWithData(
        getAgreementTypesApi(abortController.signal),
      );
      if (abortController.signal.aborted) {
        return;
      }
      if (resp?.success && resp?.typesOfAgreement) {
        const uniqueValues = Array.from(new Set([...resp.typesOfAgreement]));
        const temp = uniqueValues.map((item) => ({ label: item, value: item }));
        setAgreementTypeOptions((prev) => [...prev, ...temp]);
      }
    };

    getAgreementTypes();

    return () => abortController.abort();
  }, []);

  useEffect(() => {
    if (listing.agreementType || listing.statusType || listing.estamp) {
      fetchAgreementsList({ page: 1 });
      setUiConfigs((prevState) => ({
        ...prevState,
        page: 1,
      }));
    } else if (listing.estamp) {
      fetchAgreementsList({ page: 1 });
      setUiConfigs((prevState) => ({
        ...prevState,
        page: 1,
      }));
    } else if (listing.startDate && listing.endDate) {
      fetchAgreementsList({ page: 1 });
      setUiConfigs((prevState) => ({
        ...prevState,
        page: 1,
      }));
    } else {
      if (listing.startDate === null && listing.endDate === null) {
        fetchAgreementsList({});
      }
    }
  }, [listing.sortBy, listing.sortDirection]);

  useEffect(() => {
    fetchAgreementsList({});
  }, [uiConfigs.page]);
  const handleCsvDownload = async () => {
    csvAbortRef.current?.abort();
    const abortController = new AbortController();
    csvAbortRef.current = abortController;

    try {
      setExportButtonLoading(true);
      const filterOptions = buildFilterOptions(appliedFilters);
      const sortOption = { [sortByOptions.key]: sortByOptions.value };
      const response = await apiWrapWithErrorWithData(
        getAgreementsSigningCsvDownload({
          filterOptions,
          sortByOptions: sortOption,
          signal: abortController.signal,
        }),
      );
      if (abortController.signal.aborted) {
        return;
      }
      if (response.success) {
        if (response?.agreements?.length > 0) {
          const columnHeaders = Object.keys(response.agreements[0]);
          csvDownload({
            data: response.agreements,
            headers: columnHeaders,
            name: "agreements pending signature",
          });
        } else {
          showNotification({
            title: "Agreement csv download",
            message: "No agreement available",
            color: "red",
          });
        }
      }
      setExportButtonLoading(false);
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }
      setExportButtonLoading(false);
      showNotification({
        title: "Agreement csv download",
        message: "Could not load agreement csv",
        color: "red",
      });
    }
  };

  const [sortByOptions, setSortByOptions] = useState({
    key: "createdAt",
    value: "desc",
  });
  const handleKeyChange = (val) => {
    const newSortOption = { key: val, value: sortByOptions.value };
    setSortByOptions(newSortOption);
  };
  const toggleOrder = () => {
    const newSortOption = {
      key: sortByOptions.key,
      value: sortByOptions.value === "asc" ? "desc" : "asc",
    };
    setSortByOptions(newSortOption);
  };
  const [filters, setFilter] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [businessUnitOptions, setBusinessUnitOptions] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [reviewAgreementpage, setReviewAgreementpage] = useState(1);
  let serialNumber = (reviewAgreementpage - 1) * pageSize + 1;
  const [configs, setConfigs] = useState({
    loading: loadingStates.LOADING,
    data: null,
  });
  const isBusy =
    configs.loading === loadingStates.LOADING || exportButtonLoading;
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isBusy) return;
    setProgress(20);
    const id = setInterval(
      () => setProgress((p) => (p < 90 ? p + 10 : p)),
      200,
    );
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const onPopState = () =>
      window.history.pushState(null, "", window.location.href);
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("popstate", onPopState);
    return () => {
      clearInterval(id);
      setProgress(0);
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("popstate", onPopState);
    };
  }, [isBusy]);

  const buildFilterOptions = useCallback((filterState) => {
    const filterOptions = {};

    if (filterState.typeOfAgreement) {
      filterOptions.typeOfAgreement = { equals: filterState.typeOfAgreement };
    }
    if (filterState.mapBusinessunit) {
      filterOptions.mapBusinessunit = { equals: filterState.mapBusinessunit };
    }
    if (filterState.pendingFrom) {
      filterOptions.pendingFrom = { equals: filterState.pendingFrom };
    }
    if (
      filterState.pendingFrom === "Second Party" &&
      filterState.secondPartyName
    ) {
      filterOptions.secondPartyName = filterState.secondPartyName;
    }

    return filterOptions;
  }, []);

  const handleFilterChange = (key) => (val) => {
    setFilter((prev) => {
      const next = { ...prev, [key]: val };
      if (key === "pendingFrom" && val !== "Second Party") {
        next.secondPartyName = null;
      }
      return next;
    });
  };

  const handleSetFilter = (updater) => {
    setReviewAgreementpage(1);
    setFilter((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setAppliedFilters(next);
      return next;
    });
  };

  const handleSearch = () => {
    setAppliedFilters({ ...filters });
    setReviewAgreementpage(1);
  };

  const clearFilters = () => {
    setFilter(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setReviewAgreementpage(1);
  };

  useEffect(() => {
    const abortController = new AbortController();

    const fetchBusinessUnits = async () => {
      try {
        const { data } = await getBussinessUnit(abortController.signal);
        if (abortController.signal.aborted) {
          return;
        }
        if (data?.result) {
          setBusinessUnitOptions(
            data.result.map((unit) => ({
              value: String(unit.id),
              label: unit.name,
            })),
          );
        }
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }
        console.error("Error fetching business units:", error);
      }
    };
    fetchBusinessUnits();

    return () => abortController.abort();
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async () => {
      setConfigs((prev) => ({
        ...prev,
        loading: loadingStates.LOADING,
      }));
      try {
        const filterOptions = buildFilterOptions(appliedFilters);
        const sortOption = { [sortByOptions.key]: sortByOptions.value };
        const response = await apiWrapWithErrorWithData(
          getAgreementsInSigningList({
            filterOptions,
            page: reviewAgreementpage,
            pageSize: pageSize,
            sortByOptions: sortOption,
            signal: abortController.signal,
          }),
        );
        if (abortController.signal.aborted) {
          return;
        }
        if (response) {
          setConfigs({
            loading: loadingStates.NO_ACTIVE_REQUEST,
            data: response,
          });
        } else {
          setConfigs((prev) => ({
            ...prev,
            loading: loadingStates.NO_ACTIVE_REQUEST,
          }));
        }
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }
        console.error("Error fetching data: ", error);
        setConfigs((prev) => ({
          ...prev,
          loading: loadingStates.NO_ACTIVE_REQUEST,
        }));
      }
    };
    fetchData();

    return () => abortController.abort();
  }, [
    reviewAgreementpage,
    pageSize,
    appliedFilters,
    sortByOptions,
    buildFilterOptions,
  ]);

  useEffect(() => {
    return () => {
      listAbortRef.current?.abort();
      csvAbortRef.current?.abort();
    };
  }, []);

  return (
    <>
      <Modal
        overflow="inside"
        opened={showModalRelated}
        onClose={() => {
          setShowModalRelated(false);
        }} // Call the handler on modal close
        size="calc(80vw)"
      >
        <RelatedAgreementModal agreementId={relatedAgreementId} />
      </Modal>
      <Modal
        overflow="inside"
        opened={showModal}
        onClose={() => setShowModal(false)} // Call the handler on modal close
        size="calc(80vw)"
      >
        <AgreementAuditLogModal agreementId={agreementId} />
      </Modal>

      <div className="relative w-full min-h-[200px] space-y-6">
        <LoadingOverlay
          visible={isBusy}
          overlayBlur={2}
          loader={
            <div className="w-56 text-center">
              <Text size="sm" mb="xs">
                {exportButtonLoading ? "Exporting" : "Loading"}... {progress}%
              </Text>
              <Progress value={progress} size="sm" animate />
            </div>
          }
        />
        {configs.data && (
          <>
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="font-['Lexend'] text-2xl font-semibold tracking-wide text-slate-900">
                  Agreements Pending Signature
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Track agreements awaiting party signatures
                </p>
              </div>
              <HamburgerMenuAgreements />
            </div>

            <div className="w-full">
              <AgreementAnalytics1
                filters={filters}
                setFilter={handleSetFilter}
                configs={configs}
              />
              <AgreementGraph2 configs={configs} />
            </div>
          </>
        )}

        {(currentUser.loginAs.id === 4 ||
          currentUser.loginAs.id === 2 ||
          currentUser.loginAs.id === 1) && (
          <div className="space-y-4">
            <div className="flex justify-between items-center gap-4 rounded-md border border-slate-200 bg-white p-2 shadow-sm">
              <h2 className="text-lg font-semibold tracking-wide text-slate-900">
                Pending Signature Table
              </h2>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                <div className="flex items-center gap-2">
                  <Select
                    value={sortByOptions.key}
                    onChange={(val) => handleKeyChange(val)}
                    className="min-w-[180px]"
                    data={[
                      { label: "created at", value: "createdAt" },
                      { label: "end date", value: "dueDateOfContract" },
                      { label: "type of agreement", value: "typeOfAgreement" },
                      { label: "agreement value", value: "agreementValue" },
                      { label: "ain", value: "id" },
                    ]}
                  />
                  <ActionIcon
                    className="h-10 w-10 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 transition hover:border-[#46BDE1] hover:bg-[#46BDE1]/10 hover:text-[#46BDE1]"
                    onClick={toggleOrder}
                  >
                    {sortByOptions.value === "asc" ? (
                      <SortAscending size={22} />
                    ) : (
                      <SortDescending size={22} />
                    )}
                  </ActionIcon>
                </div>

                <Button
                  className="h-10 rounded-lg border-0 bg-[#46BDE1] px-5 text-sm font-medium text-white shadow-sm transition hover:bg-[#3aa8ca] disabled:opacity-60"
                  disabled={isBusy}
                  onClick={handleCsvDownload}
                >
                  Export
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm">
              <AgreementTypeFilter
                value={filters.typeOfAgreement}
                onChange={handleFilterChange("typeOfAgreement")}
              />
              <Select
                placeholder="Business unit"
                value={filters.mapBusinessunit}
                data={businessUnitOptions}
                onChange={handleFilterChange("mapBusinessunit")}
                clearable
              />
              <Select
                placeholder="Pending from"
                value={filters.pendingFrom}
                data={PENDING_FROM_OPTIONS}
                onChange={handleFilterChange("pendingFrom")}
                clearable
              />
              {filters.pendingFrom === "Second Party" && (
                <PartyNameFilter
                  value={filters.secondPartyName || ""}
                  onChange={handleFilterChange("secondPartyName")}
                  listType={PARTY_NAME_LIST_TYPES.AGREEMENTS}
                  placeholder="Second party name"
                />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                className="h-9 rounded-lg border-0 bg-green-600 px-4 text-sm font-medium text-white hover:bg-green-700"
                disabled={isBusy}
                onClick={handleSearch}
              >
                Search
              </Button>
              <Button
                className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50"
                color="red"
                variant="outline"
                disabled={isBusy}
                onClick={clearFilters}
              >
                Clear
              </Button>
            </div>

            {configs.loading === loadingStates.LOADING && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="space-y-2">
                  {Array.from({ length: 10 }).map((i) => (
                    <Skeleton
                      key={i}
                      height={36}
                      radius="md"
                      className="w-full"
                    />
                  ))}
                </div>
                <div className="mt-4 flex justify-center">
                  <Skeleton height={40} radius="md" className="w-40" />
                </div>
              </div>
            )}

            {configs.loading === loadingStates.NO_ACTIVE_REQUEST &&
              configs.data?.agreements && (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="whitespace-nowrap px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Sr no.
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            AR. No.
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Business unit
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Type
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Second party
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Pending from
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Ageing (days)
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Contract date
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            End date
                          </th>
                          {/* <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            TN period
                          </th> */}
                          <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Related
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                            View
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                            History
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {configs.data.agreements.map((row) => {
                          let ageingColorClass;
                          if (row.ageing <= 3) {
                            ageingColorClass = "text-[#46BDE1]";
                          } else if (row.ageing <= 7) {
                            ageingColorClass = "text-[#46BDE1]";
                          } else if (row.ageing <= 10) {
                            ageingColorClass = "text-[#FDAE38]";
                          } else {
                            ageingColorClass = "text-[#F75435]";
                          }

                          return (
                            <tr
                              key={row.id}
                              className="transition-colors hover:bg-slate-50/80"
                            >
                              <td className="whitespace-nowrap px-2 py-3 text-slate-500">
                                {serialNumber++}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3">
                                {`AR-${row.id}`}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                                {row.businessUnit}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                                {row.agreementData.json.typeOfAgreement}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                <div className="font-medium">
                                  {row.agreementData.json.brandName}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {`(${row.agreementData?.json?.secondParty.name || "-"})`}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-flex max-w-[220px] rounded-full bg-amber-50 text-xs font-medium text-amber-700">
                                  {row.pendingFrom || "-"}
                                </span>
                              </td>
                              <td
                                className={`whitespace-nowrap px-4 py-3 text-sm font-semibold ${ageingColorClass}`}
                              >
                                {`${row.ageing}`}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                                {formatDate(row.dateOfContract)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                                {formatDate(row.dueDateOfContract)}
                              </td>
                              {/* <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                                {row.terminationNoticePeriod ?? "—"}
                              </td> */}
                              <td className="px-4 py-3 text-center">
                                {row.relationshipWith ? (
                                  <ActionIcon
                                    className="mx-auto h-9 w-9 rounded-lg border border-slate-200 bg-white text-[#46BDE1] transition hover:border-[#46BDE1] hover:bg-[#46BDE1]/10"
                                    onClick={() => {
                                      handlePlusClick(row.id);
                                    }}
                                  >
                                    <Plus size={20} />
                                  </ActionIcon>
                                ) : (
                                  <span className="text-sm text-slate-400">
                                    --
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <a
                                  href={`/app/agreements_manager/agreements/details/${row.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#46BDE1] hover:text-[#46BDE1]/80 flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200"
                                >
                                  <Eye size={20} />
                                </a>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <ActionIcon
                                  className="mx-auto h-9 w-9 rounded-lg border border-slate-200 bg-white text-[#46BDE1] transition hover:border-[#46BDE1] hover:bg-[#46BDE1]/10"
                                  onClick={() => handleSearchClick(row.id)}
                                >
                                  <History size={20} />
                                </ActionIcon>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            {configs.loading === loadingStates.NO_ACTIVE_REQUEST &&
            !configs.data?.agreements?.length ? (
              <div className="flex min-h-[300px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-center">
                <p className="text-base font-medium text-slate-600">
                  No agreements
                </p>
                <p className="mt-1 text-sm text-slate-400">
                  Agreements pending signature will appear here
                </p>
              </div>
            ) : null}

            {configs.data && (
              <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row">
                <Pagination
                  page={reviewAgreementpage}
                  onChange={(page) => !isBusy && setReviewAgreementpage(page)}
                  total={Math.ceil(configs.data?.agreementsCount / pageSize)}
                />
                <div className="flex items-center gap-3">
                  <Text className="text-sm text-slate-500">
                    Select page size
                  </Text>
                  <Select
                    className="w-24"
                    data={["10", "20", "50"]}
                    value={pageSize + ""}
                    onChange={(val) => setPageSize(parseInt(val))}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AgreementsInSigning;
